import { Link } from '@tanstack/react-router';
import { type House } from '../api/houseApi';
import { useAuth } from '../../../app/providers';
import { memo, useMemo } from 'react';

interface HouseCardProps {
  house: House;
  onEdit?: (house: House) => void;
  onDelete?: (house: House) => void;
}

export const HouseCard = memo<HouseCardProps>(({ house, onEdit, onDelete }) => {
  const { user } = useAuth();

  const canEdit = user?.role === 'ADMIN' || house.managerId === user?.id;
  const occupancyRate = house._count?.residents ? 
    Math.round((house._count.residents / house.capacity) * 100) : 0;

  // Memoized accessibility labels
  const occupancyLabel = useMemo(() => 
    `Occupancy: ${house._count?.residents || 0} of ${house.capacity} residents, ${occupancyRate}% full`,
    [house._count?.residents, house.capacity, occupancyRate]
  );

  const statusLabel = useMemo(() => 
    `House status: ${house.isActive ? 'Active' : 'Inactive'}`,
    [house.isActive]
  );

  // Memoized occupancy color classes
  const occupancyColorClasses = useMemo(() => {
    if (occupancyRate >= 90) return 'bg-red-500';
    if (occupancyRate >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  }, [occupancyRate]);

  return (
    <article 
      className="card hover:shadow-lg transition-shadow duration-200 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2"
      role="article"
      aria-labelledby={`house-${house.id}-title`}
    >
      <div className="p-4 sm:p-6">
        {/* Header - Enhanced responsive design */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-4 mb-4">
          <h3 
            id={`house-${house.id}-title`}
            className="text-lg sm:text-xl font-semibold text-text-primary break-words"
          >
            {house.name}
          </h3>
          <span 
            className={`
              inline-flex items-center px-2 py-1 rounded-full text-xs font-medium 
              whitespace-nowrap self-start sm:ml-2
              high-contrast:border high-contrast:border-gray-600
              ${house.isActive 
                ? 'bg-green-100 text-green-800 high-contrast:bg-green-200 high-contrast:text-green-900' 
                : 'bg-red-100 text-red-800 high-contrast:bg-red-200 high-contrast:text-red-900'
              }
            `}
            aria-label={statusLabel}
          >
            {house.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        
        {/* Address - Enhanced responsive layout */}
        <div className="space-y-3 mb-4">
          <div className="flex items-start gap-2">
            <span 
              className="text-gray-500 mt-0.5 flex-shrink-0" 
              aria-hidden="true"
              role="img"
              aria-label="Location"
            >
              📍
            </span>
            <address className="text-text-secondary text-sm break-words not-italic">
              {house.address}, {house.city}, {house.state} {house.zipCode}
            </address>
          </div>
          
          {/* Manager */}
          <div className="flex items-center gap-2">
            <span 
              className="text-gray-500 flex-shrink-0" 
              aria-hidden="true"
              role="img"
              aria-label="Manager"
            >
              👤
            </span>
            <span className="text-text-secondary text-sm">
              Manager: <span className="font-medium">{house.manager.name}</span>
            </span>
          </div>
          
          {/* Capacity - Enhanced responsive design */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2">
              <span 
                className="text-gray-500 flex-shrink-0" 
                aria-hidden="true"
                role="img"
                aria-label="Capacity"
              >
                🏠
              </span>
              <span className="text-text-secondary text-sm">
                Capacity: <span className="font-medium">{house._count?.residents || 0}/{house.capacity}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 ml-6 sm:ml-0">
              <div 
                className="w-16 sm:w-20 bg-gray-200 rounded-full h-2 high-contrast:border high-contrast:border-gray-600"
                role="progressbar"
                aria-valuenow={occupancyRate}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={occupancyLabel}
              >
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${occupancyColorClasses}`}
                  style={{ width: `${Math.min(occupancyRate, 100)}%` }}
                />
              </div>
              <span className="text-xs text-text-secondary font-medium min-w-[2rem] text-right">
                {occupancyRate}%
              </span>
            </div>
          </div>
        </div>

        {/* Description */}
        {house.description && (
          <p className="text-text-secondary text-sm mb-4 line-clamp-2 leading-relaxed">
            {house.description}
          </p>
        )}

        {/* Amenities - Enhanced accessibility */}
        {house.amenities && house.amenities.length > 0 && (
          <div className="mb-4">
            <div 
              className="flex flex-wrap gap-1 sm:gap-2"
              role="list"
              aria-label="House amenities"
            >
              {house.amenities.slice(0, 3).map((amenity, index) => (
                <span 
                  key={index}
                  role="listitem"
                  className="
                    px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full
                    high-contrast:bg-blue-200 high-contrast:text-blue-900 
                    high-contrast:border high-contrast:border-blue-600
                  "
                >
                  {amenity}
                </span>
              ))}
              {house.amenities.length > 3 && (
                <span 
                  role="listitem"
                  className="
                    px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full
                    high-contrast:bg-gray-200 high-contrast:text-gray-900 
                    high-contrast:border high-contrast:border-gray-600
                  "
                  aria-label={`${house.amenities.length - 3} additional amenities`}
                >
                  +{house.amenities.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actions - Enhanced responsive design and accessibility */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-gray-100">
          <Link
            // @ts-expect-error - Dynamic route parameter
            to={`/houses/${house.id}`}
            className="
              btn-outline flex-1 text-center min-h-[44px] sm:min-h-[40px]
              focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
              transition-all duration-200
            "
            aria-describedby={`house-${house.id}-title`}
          >
            View Details
          </Link>
          {canEdit && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 sm:flex-shrink-0">
              {onEdit ? (
                <button
                  onClick={() => onEdit(house)}
                  className="
                    btn-secondary min-h-[44px] sm:min-h-[40px]
                    focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                    transition-all duration-200
                  "
                  aria-label={`Edit ${house.name}`}
                >
                  Edit
                </button>
              ) : (
                <Link
                  // @ts-expect-error - Dynamic route parameter
                  to={`/houses/${house.id}/edit`}
                  className="
                    btn-secondary min-h-[44px] sm:min-h-[40px]
                    focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                    transition-all duration-200
                  "
                  aria-label={`Edit ${house.name}`}
                >
                  Edit
                </Link>
              )}
              {onDelete && user?.role === 'ADMIN' && (
                <button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to delete ${house.name}? This action cannot be undone.`)) {
                      onDelete(house);
                    }
                  }}
                  className="
                    btn-danger min-h-[44px] sm:min-h-[40px] min-w-[44px] sm:min-w-[40px]
                    focus:ring-2 focus:ring-red-500 focus:ring-offset-2
                    transition-all duration-200 flex items-center justify-center
                  "
                  aria-label={`Delete ${house.name}`}
                  title={`Delete ${house.name}`}
                >
                  <span role="img" aria-label="Delete">🗑️</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
});

HouseCard.displayName = 'HouseCard';